<template>
  <div class="modal d-block" style="background: rgba(0, 0, 0, 0.5)" @click.self="$emit('close')">
    <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            {{ editMode ? 'Edit' : 'Add' }} Menstrual Record
          </h5>
          <button type="button" class="btn-close" @click="$emit('close')"></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="handleSubmit">
            <!-- Start Date -->
            <div class="mb-3">
              <label class="form-label">Period Start Date *</label>
              <input
                type="date"
                class="form-control"
                v-model="formData.start_date"
                :max="todayDate"
                required
              />
            </div>

            <!-- End Date -->
            <div class="mb-3">
              <label class="form-label">Period End Date</label>
              <input
                type="date"
                class="form-control"
                v-model="formData.end_date"
                :min="formData.start_date"
                :max="todayDate"
              />
              <small class="text-muted">Leave empty if period is ongoing</small>
            </div>

            <!-- Flow Level -->
            <div class="mb-3">
              <label class="form-label">Flow Level *</label>
              <div class="flow-options">
                <div
                  v-for="flow in flowOptions"
                  :key="flow.value"
                  class="flow-option"
                  :class="{ active: formData.flow_level === flow.value }"
                  @click="formData.flow_level = flow.value"
                >
                  <div class="flow-indicator" :style="{ backgroundColor: flow.color }"></div>
                  <span>{{ flow.label }}</span>
                </div>
              </div>
            </div>

            <!-- Symptoms -->
            <div class="mb-3">
              <label class="form-label">Symptoms (Select all that apply)</label>
              <div class="symptoms-grid">
                <button
                  v-for="symptom in symptomOptions"
                  :key="symptom"
                  type="button"
                  class="btn btn-sm"
                  :class="formData.symptoms.includes(symptom) ? 'btn-danger' : 'btn-outline-secondary'"
                  @click="toggleSymptom(symptom)"
                >
                  {{ symptom }}
                </button>
              </div>
            </div>

            <!-- Mood -->
            <div class="mb-3">
              <label class="form-label">Mood</label>
              <select class="form-select" v-model="formData.mood">
                <option value="">Select mood...</option>
                <option value="happy">😊 Happy</option>
                <option value="neutral">😐 Neutral</option>
                <option value="sad">😢 Sad</option>
                <option value="anxious">😰 Anxious</option>
                <option value="irritable">😤 Irritable</option>
              </select>
            </div>

            <!-- Notes -->
            <div class="mb-3">
              <label class="form-label">Notes</label>
              <textarea
                class="form-control"
                rows="3"
                v-model="formData.note"
                placeholder="Any additional information about your symptoms, activities, or feelings..."
                maxlength="500"
              ></textarea>
              <small class="text-muted">{{ formData.note.length }}/500 characters</small>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="$emit('close')">
            Cancel
          </button>
          <button type="button" class="btn btn-primary" @click="handleSubmit">
            <i class="bi bi-check-lg me-1"></i>Save Record
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RecordForm',
  props: {
    record: {
      type: Object,
      default: null
    },
    editMode: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'save'],
  data() {
    return {
      formData: {
        start_date: '',
        end_date: '',
        flow_level: 'medium',
        symptoms: [],
        mood: '',
        note: ''
      },
      flowOptions: [
        { value: 'light', label: 'Light Flow', color: '#ffcccb' },
        { value: 'medium', label: 'Medium Flow', color: '#ff7f7f' },
        { value: 'heavy', label: 'Heavy Flow', color: '#dc3545' }
      ],
      symptomOptions: [
        'Cramps',
        'Headache',
        'Mood Swings',
        'Fatigue',
        'Back Pain',
        'Breast Tenderness',
        'Bloating',
        'Nausea',
        'Acne',
        'Food Cravings'
      ]
    }
  },
  computed: {
    todayDate() {
      return new Date().toISOString().split('T')[0]
    }
  },
  mounted() {
    if (this.editMode && this.record) {
      // Load existing record data
      this.formData = {
        start_date: this.record.start_date?.toDate?.()?.toISOString().split('T')[0] || this.record.start_date,
        end_date: this.record.end_date?.toDate?.()?.toISOString().split('T')[0] || this.record.end_date || '',
        flow_level: this.record.flow_level || 'medium',
        symptoms: this.record.symptoms || [],
        mood: this.record.mood || '',
        note: this.record.note || ''
      }
    }
  },
  methods: {
    toggleSymptom(symptom) {
      const index = this.formData.symptoms.indexOf(symptom)
      if (index > -1) {
        this.formData.symptoms.splice(index, 1)
      } else {
        this.formData.symptoms.push(symptom)
      }
    },
    handleSubmit() {
      // Validation
      if (!this.formData.start_date) {
        alert('Please select a start date')
        return
      }

      // Emit save event with form data
      this.$emit('save', this.formData)
    }
  }
}
</script>

<style scoped>
.modal {
  z-index: 1055;
}

.flow-options {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.flow-option {
  display: flex;
  align-items: center;
  padding: 0.75rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.flow-option:hover {
  border-color: #dc3545;
  background-color: #f8f9fa;
}

.flow-option.active {
  border-color: #dc3545;
  background-color: rgba(220, 53, 69, 0.1);
}

.flow-indicator {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  margin-right: 0.75rem;
  border: 2px solid #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.symptoms-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 0.5rem;
}

@media (max-width: 768px) {
  .symptoms-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .modal-dialog {
    margin: 0.5rem;
  }
}
</style>
